<template>
  <a-modal 
    v-model="innerVisible" 
    title="节点关系可视化 (支持拖拽)" 
    width="98%" 
    :footer="null" 
    centered 
    @cancel="handleClose" 
    bodyStyle="padding: 0; background: #f0f2f5;"
  >
    <div class="visual-container" @mousemove="onMouseMove" @mouseup="onMouseUp" @mouseleave="onMouseUp">
      <div class="toolbar">
        <a-space>
          <a-tag color="#52c41a">起始节点</a-tag>
          <a-tag color="#1890ff">线性跳转</a-tag>
          <a-tag color="#722ed1">分支选择</a-tag>
          <a-tag color="#f5222d">结束节点</a-tag>
          <span class="hint">💡 提示：左键按住节点可自由拖动，修正重叠</span>
          <a-button type="primary" size="small" icon="deployment-unit" @click="initLayout">重置自动布局</a-button>
        </a-space>
      </div>

      <div class="canvas-viewport" ref="viewport">
        <div class="canvas-content" :style="canvasStyle">
          <svg class="line-svg">
            <defs>
              <marker id="arrow" markerWidth="10" markerHeight="10" refX="8" refY="5" orient="auto">
                <path d="M0,0 L10,5 L0,10 Z" fill="#999" />
              </marker>
            </defs>
            <g v-for="(line, i) in lines" :key="'line-' + i">
              <path 
                :d="line.path" 
                :stroke="line.type === 'option' ? '#722ed1' : '#1890ff'" 
                stroke-width="1.5" 
                fill="none" 
                marker-end="url(#arrow)" 
              />
              <text v-if="line.label" :x="line.labelX" :y="line.labelY" class="line-label">
                {{ line.label }}
              </text>
            </g>
          </svg>

          <div 
            v-for="node in layoutNodes" 
            :key="String(node.id)" 
            class="node-box" 
            :class="getNodeClass(node)"
            :style="{ left: node.x + 'px', top: node.y + 'px' }"
            @mousedown="onMouseDown($event, node)"
          >
            <div class="node-header">
              <span class="node-tag" v-if="node.isStart">START</span>
              <span class="node-id">#{{ String(node.id).slice(-6) }}</span>
            </div>
            <div class="node-name" :title="node.nodeName">{{ node.nodeName }}</div>
            <div class="node-footer" v-if="node.options && node.options.length">
              {{ node.options.length }} 个分支
            </div>
          </div>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script>
export default {
  name: 'NodeVisualModal',
  props: { 
    visible: Boolean, 
    nodes: { type: Array, default: () => [] } 
  },
  data() {
    return {
      innerVisible: false,
      layoutNodes: [],
      lines: [],
      draggingNode: null,
      offset: { x: 0, y: 0 },
      canvasSize: { w: 4000, h: 3000 } // 初始画布大小
    };
  },
  computed: {
    canvasStyle() {
      return { width: this.canvasSize.w + 'px', height: this.canvasSize.h + 'px' };
    }
  },
  watch: {
    visible(val) {
      this.innerVisible = val;
      if (val) {
        this.$nextTick(() => {
          this.initLayout();
        });
      }
    },
    // 监听节点位置变化，实时更新连线
    layoutNodes: {
      deep: true,
      handler() { this.updateLines(); }
    }
  },
  methods: {
    handleClose() { 
      this.$emit('update:visible', false); 
    },

    // 核心布局算法：BFS 分层
    initLayout() {
      if (!this.nodes || this.nodes.length === 0) return;
      
      const config = { nodeW: 200, nodeH: 90, levelGap: 320, rowGap: 140 };
      const nodesMap = {};
      
      // 1. 初始化，ID 全部转字符串处理防止精度丢失
      this.nodes.forEach(n => {
        const sid = String(n.id);
        nodesMap[sid] = { ...n, id: sid, x: 0, y: 0, level: -1 };
      });

      // 2. 层级计算
      const queue = [];
      const starts = Object.values(nodesMap).filter(n => n.isStart);
      
      (starts.length ? starts : [Object.values(nodesMap)[0]]).forEach(s => {
        nodesMap[s.id].level = 0;
        queue.push(s.id);
      });

      while (queue.length > 0) {
        const currId = queue.shift();
        const curr = nodesMap[currId];

        const nextIds = [];
        if (curr.nextNodeId) nextIds.push(String(curr.nextNodeId));
        if (curr.options) {
          curr.options.forEach(opt => {
            if (opt.nextNodeId) nextIds.push(String(opt.nextNodeId));
          });
        }

        nextIds.forEach(id => {
          if (nodesMap[id] && nodesMap[id].level === -1) {
            nodesMap[id].level = curr.level + 1;
            queue.push(id);
          }
        });
      }

      // 3. 确定坐标
      const levelCounter = {};
      this.layoutNodes = Object.values(nodesMap).map(node => {
        const lvl = node.level === -1 ? 0 : node.level;
        if (levelCounter[lvl] === undefined) levelCounter[lvl] = 0;
        
        node.x = lvl * config.levelGap + 60;
        node.y = levelCounter[lvl] * config.rowGap + 100;
        levelCounter[lvl]++;
        return node;
      });

      // 动态调整画布高度
      const maxRows = Math.max(...Object.values(levelCounter));
      this.canvasSize.h = Math.max(2000, maxRows * config.rowGap + 600);
      this.updateLines();
    },

    updateLines() {
      const lines = [];
      const nodeW = 200; 
      const nodeH = 90;

      this.layoutNodes.forEach(s => {
        // 线性连线
        if (s.nextNodeId) {
          const targetId = String(s.nextNodeId);
          const t = this.layoutNodes.find(n => n.id === targetId);
          if (t) lines.push(this.calculatePath(s, t, 'linear', nodeW, nodeH));
        }
        // 分支连线
        if (s.options && s.options.length) {
          s.options.forEach(opt => {
            if (opt.nextNodeId) {
              const targetId = String(opt.nextNodeId);
              const t = this.layoutNodes.find(n => n.id === targetId);
              if (t) lines.push(this.calculatePath(s, t, 'option', nodeW, nodeH, opt.optionName));
            }
          });
        }
      });
      this.lines = lines;
    },

    calculatePath(s, t, type, nw, nh, label = '') {
      const sx = s.x + nw;
      const sy = s.y + nh / 2;
      const tx = t.x;
      const ty = t.y + nh / 2;
      
      const dist = Math.abs(tx - sx);
      const cp1x = sx + dist * 0.4;
      const cp2x = tx - dist * 0.4;
      
      return {
        path: `M ${sx} ${sy} C ${cp1x} ${sy}, ${cp2x} ${ty}, ${tx} ${ty}`,
        type,
        label,
        labelX: sx + 15,
        labelY: sy - 10
      };
    },

    onMouseDown(e, node) {
      this.draggingNode = node;
      this.offset = { x: e.clientX - node.x, y: e.clientY - node.y };
    },
    onMouseMove(e) {
      if (!this.draggingNode) return;
      this.draggingNode.x = e.clientX - this.offset.x;
      this.draggingNode.y = e.clientY - this.offset.y;
    },
    onMouseUp() { this.draggingNode = null; },

    getNodeClass(node) {
      if (node.isStart) return 'node-start';
      const hasOut = node.nextNodeId || (node.options && node.options.length);
      if (!hasOut) return 'node-end';
      if (node.options && node.options.length) return 'node-branch';
      return 'node-normal';
    }
  }
};
</script>

<style scoped>
.visual-container {
  position: relative;
  height: 85vh;
  overflow: hidden;
  font-family: sans-serif;
}

.toolbar {
  background: #fff;
  padding: 12px 24px;
  border-bottom: 1px solid #ddd;
  z-index: 100;
  position: relative;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.hint {
  font-size: 12px;
  color: #8c8c8c;
  margin-left: 10px;
}

.canvas-viewport {
  height: calc(85vh - 55px);
  overflow: auto;
  background: #f8f9fa;
}

.canvas-content {
  position: relative;
  background-image: radial-gradient(#d1d1d1 1px, transparent 0);
  background-size: 40px 40px;
}

.line-svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.line-label {
  font-size: 11px;
  fill: #722ed1;
  font-weight: bold;
  paint-order: stroke;
  stroke: #f8f9fa;
  stroke-width: 3px;
}

.node-box {
  position: absolute;
  width: 200px;         /* 宽度加宽 */
  min-height: 90px;     /* 改为最小高度 */
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  padding: 12px;
  cursor: grab;
  z-index: 5;
  user-select: none;
  border-top: 5px solid #d9d9d9;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s;
}

.node-box:active {
  cursor: grabbing;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

/* 状态颜色 */
.node-start { border-top-color: #52c41a; }
.node-branch { border-top-color: #722ed1; }
.node-normal { border-top-color: #1890ff; }
.node-end { border-top-color: #f5222d; }

.node-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.node-tag {
  background: #52c41a;
  color: #fff;
  font-size: 9px;
  padding: 0 4px;
  border-radius: 2px;
  font-weight: bold;
}

.node-id {
  font-size: 10px;
  color: #bfbfbf;
  font-family: monospace;
}

.node-name {
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  margin: 4px 0;
  /* 重点修复：允许换行 */
  white-space: normal; 
  word-break: break-all;
  line-height: 1.4;
  flex-grow: 1;
}

.node-footer {
  font-size: 11px;
  color: #8c8c8c;
  border-top: 1px solid #f0f0f0;
  padding-top: 6px;
  margin-top: 4px;
}
</style>